import {
  Card,
  Checkbox,
  CrudMode,
  DataArray,
  DataDisplay,
  InfoBanner,
  pn,
  Stack,
  Typography,
  ValidationErrors,
} from "@compass/core";
import { Select } from "components";
import { LkRole, LkRoleId, LkUserPartnerAssignmentTypeId, User, UserDto, UserEditResource } from "generated/openApi";
import produce from "immer";
import "moment-timezone";

export const MatchAssignment = (props: {
  crudMode: CrudMode;
  userDto: UserDto;
  appUserRoleId: number | undefined;
  setUserDto: (value: UserDto) => void;
  editResource: UserEditResource;
  validationErrors: ValidationErrors;
  selectedRole: LkRole | undefined;
}) => {
  const UNRESTRICTED: string = "Unrestricted (All)";
  const sortedSubPartners = [...(props.editResource.subPartners ?? [])].sort((a, b) => {
    const partnerComparison = (a.partner?.name ?? "").localeCompare(b.partner?.name ?? "");
    return partnerComparison || (a.name ?? "").localeCompare(b.name ?? "");
  });

  return (
    <Card transparentHeader={true} header="Match Assignment" fullHeight>
      <Stack spacing="md">
        <Stack>
          <InfoBanner title="">
          <Typography variant="caption" color="primary">
            Match data can be configured for {props.selectedRole?.name} role to allow only access to specified Partners
            or Sub-Partners. Leave blank for unrestricted access to all data.
          </Typography>
          </InfoBanner>
        </Stack>

        <Stack>
          {(props.crudMode === "view" || (props.appUserRoleId != LkRoleId.Cobro && props.appUserRoleId != LkRoleId.Administrator)) && (
            <DataDisplay>
              {props.userDto.user!.partnerAssignmentType?.id ? props.userDto.user!.partnerAssignmentType?.name : UNRESTRICTED}
            </DataDisplay>
          )}

          {(props.crudMode === "edit" &&
            <Select
              disabled={props.appUserRoleId != LkRoleId.Cobro && props.appUserRoleId != LkRoleId.Administrator}
              placeholder={UNRESTRICTED}
              value={props.editResource.userPartnerAssignmentTypes?.find(
                (x) => x.id === props.userDto.user!.partnerAssignmentTypeId
              )}
              options={props.editResource.userPartnerAssignmentTypes}
              getOptionLabel={(option) => option.name}
              onChange={(e, value) =>
                props.setUserDto(
                  produce(props.userDto, (draft) => {
                    draft.user!.partnerAssignmentTypeId = value?.id;
                  })
                )
              }
              errors={props.validationErrors[pn<User>("partnerAssignmentTypeId")]}
            />
          )}
        </Stack>

        <Stack>
          {props.userDto.user!.partnerAssignmentTypeId === Number(LkUserPartnerAssignmentTypeId.Partner) && (
            <>
              {props.crudMode === "view" && <DataArray value={props.userDto.user!.partners?.map((x) => x.name)} />}

              {props.crudMode === "edit" &&
                props.editResource.partners?.map((x) => (
                  <Stack>
                    <Checkbox
                      key={x.id}
                      disabled={props.appUserRoleId != LkRoleId.Cobro && props.appUserRoleId != LkRoleId.Administrator}
                      checked={props.userDto.user!.partners?.map((x) => x.id).includes(x.id)}
                      onClick={() =>
                        props.setUserDto(
                          produce(props.userDto, (draft) => {
                            const index: number | undefined = props.userDto.user!.partners?.map((x) => x.id).indexOf(x.id);
                            if (index !== undefined && index >= 0) draft.user!.partners?.splice(index, 1);
                            else draft.user!.partners?.push(x);
                          })
                        )
                      }
                      label={x.name}
                    />
                  </Stack>
                ))}
            </>
          )}

          {props.userDto.user!.partnerAssignmentTypeId === Number(LkUserPartnerAssignmentTypeId.SubPartner) && (
            <>
              {props.crudMode === "view" && <DataArray value={props.userDto.user!.subPartners?.map((x) => x.name)} />}

              {props.crudMode === "edit" &&
                sortedSubPartners.map((subPartner) => (
                  <Checkbox
                    key={subPartner.id}
                    disabled={props.appUserRoleId != LkRoleId.Cobro && props.appUserRoleId != LkRoleId.Administrator}
                    checked={props.userDto.user!.subPartners?.map((x) => x.id).includes(subPartner.id)}
                    onClick={() =>
                      props.setUserDto(
                        produce(props.userDto, (draft) => {
                          const index: number | undefined = props.userDto.user!.subPartners
                            ?.map((x) => x.id)
                            .indexOf(subPartner.id);
                          if (index !== undefined && index >= 0) draft.user!.subPartners?.splice(index, 1);
                          else draft.user!.subPartners?.push(subPartner);
                        })
                      )
                    }
                    label={
                      <Typography component="span">
                        <Typography component="span" fontWeight="bold">
                          {subPartner.partner?.name}:
                        </Typography>{" "}
                        {subPartner.name}
                      </Typography>
                    }
                  />
                ))}
            </>
          )}
        </Stack>
      </Stack>
    </Card>
  );
};
